import React, { useMemo } from "react";

import { faCheck, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { CustomPopover } from "src/base-components/Popover";
import { useOptionListEditor } from "src/cases/caseView/blocks/cellEditors/useOptionListEditor";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";

export const TagsEditor: React.FC<{
  value: unknown;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, columnValues, onChange, onBlur, hasLeftIcon }) => {
  const currentValue = useMemo(() => {
    if (Array.isArray(value)) return value.map(String);
    if (value !== null && value !== undefined) return [String(value)];
    return [];
  }, [value]);

  const options = useMemo(
    () =>
      Array.from(
        new Set([
          ...(columnValues ?? [])
            .flatMap((v) => (Array.isArray(v) ? v : [v]))
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v)),
          ...currentValue,
        ]),
      ),
    [columnValues, currentValue],
  );

  const {
    isAddingValue,
    setIsAddingValue,
    search,
    setSearch,
    filteredOptions,
    focusedIndex,
    setFocusedIndex,
    listRef,
    triggerRef,
    moveFocus,
    exitAddMode,
  } = useOptionListEditor({ options, onBlur });

  const handleToggleTag = (tag: string) => {
    const newValue = currentValue.includes(tag)
      ? currentValue.filter((v) => v !== tag)
      : [...currentValue, tag];
    onChange(newValue);
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(e.key === "ArrowDown" ? "down" : "up");
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex < filteredOptions.length) {
          handleToggleTag(filteredOptions[focusedIndex]);
        } else {
          setIsAddingValue(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        exitAddMode();
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex < filteredOptions.length) {
          handleToggleTag(filteredOptions[focusedIndex]);
          setSearch("");
        } else if (search) {
          if (!currentValue.includes(search)) {
            onChange([...currentValue, search]);
          }
          setSearch("");
          setIsAddingValue(false);
        } else if (filteredOptions.length > 0) {
          handleToggleTag(filteredOptions[0]);
          setSearch("");
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(e.key === "ArrowDown" ? "down" : "up");
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full">
      <CustomPopover
        button={
          isAddingValue ? (
            <div
              className={twJoin(
                "flex h-full w-full min-w-0 items-center gap-1 py-1.5 pr-2",
                hasLeftIcon ? "pl-6" : "pl-1.5",
              )}
            >
              <input
                ref={(node) => node?.focus()}
                aria-autocomplete="list"
                aria-expanded={true}
                aria-multiselectable={true}
                className="h-full w-full bg-transparent font-inter-normal-12px outline-none"
                placeholder="Type new value..."
                role="combobox"
                value={search}
                onBlur={onBlur}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          ) : (
            <div
              className={twJoin(
                "flex h-full w-full min-w-0 items-center gap-1 py-1.5 pr-2",
                hasLeftIcon ? "pl-6" : "pl-1.5",
              )}
            >
              {currentValue.length > 0 && (
                <>
                  <Pill size="sm" variant="gray">
                    <Pill.Text>{currentValue[0]}</Pill.Text>
                  </Pill>
                  {currentValue.length > 1 && (
                    <Pill size="sm" variant="gray">
                      <Pill.Text>+{currentValue.length - 1}</Pill.Text>
                    </Pill>
                  )}
                </>
              )}
            </div>
          )
        }
        className="decideScrollbar max-h-48 w-48 overflow-auto"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listRef}
          className="outline-none"
          role="listbox"
          tabIndex={isAddingValue ? undefined : 0}
          onKeyDown={!isAddingValue ? handleListKeyDown : undefined}
        >
          {filteredOptions.map((opt, index) => {
            const isSelected = currentValue.includes(opt);
            return (
              <button
                key={opt}
                aria-selected={isSelected}
                className={twJoin(
                  "flex w-full items-center justify-between px-4 py-2.5 text-left",
                  index === focusedIndex ? "bg-gray-50" : "hover:bg-gray-50",
                )}
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleToggleTag(opt);
                }}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <Pill size="sm" variant="gray">
                  <Pill.Text>{opt}</Pill.Text>
                </Pill>
                {isSelected && (
                  <Icon icon={faCheck} size="xs" variant="active" />
                )}
              </button>
            );
          })}
          {filteredOptions.length === 0 && !search && (
            <div className="px-4 py-2.5 font-inter-normal-13px text-gray-400">
              No tags
            </div>
          )}
          {filteredOptions.length > 0 && (
            <div className="my-1 border-gray-100 border-t" />
          )}
          {isAddingValue && search ? (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px",
                currentValue.includes(search)
                  ? "cursor-not-allowed opacity-50"
                  : focusedIndex === filteredOptions.length
                    ? "bg-gray-50 text-gray-800"
                    : "text-gray-800 hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                if (currentValue.includes(search)) return;
                onChange([...currentValue, search]);
                setSearch("");
                setIsAddingValue(false);
              }}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add</span>
              <Pill size="sm" variant="gray">
                <Pill.Text>{search}</Pill.Text>
              </Pill>
            </button>
          ) : (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                !isAddingValue && focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsAddingValue(true);
              }}
              onMouseEnter={() => setFocusedIndex(filteredOptions.length)}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add value</span>
            </button>
          )}
        </div>
      </CustomPopover>
    </div>
  );
};
